import { Autocomplete, Chip, TextField } from "@mui/material";
import { Axios } from "api/axios";
import { ItemType } from "assets/types/itemTypes";
import SubmitButton from "Components/Buttons/SubmitButton";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import { LoadingCard } from "Components/LoadingCard/LoadingCard";
import { QuantityInput } from "Components/Other/quantityInput";
import { useFormik } from "formik";
import { calculateItemValues } from "helpers/functions/calculateItemValues";
import { formatLabel } from "helpers/functions/formatLabel";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchLatestItemById } from "slices/item/thunk";
import {
  addTemplate,
  fetchTemplateById,
  updateTemplate,
} from "slices/template/thunk";
import * as Yup from "yup";
const DynamicItems = lazy(() => import("Components/DynamicComp/DynamicItems"));

type AttributesTypes = {
  id: string;
  name: string;
};

type Category = {
  category_id: string;
  category_name: string;
  is_mandatory: boolean;
  attributes: AttributesTypes[];
};

const RESTRICTED_SINGLE_ITEM_CATEGORIES = [
  { category_id: "743cffc9-09ae-4509-be91-314361e7bb33", category_name: "CPU" },
  // {
  //   category_id: "54f1c6b9-481a-4a16-a70e-bfe49341adf8",
  //   category_name: "Memory",
  // },
  {
    category_id: "14872373-f2f1-45ed-802e-81ef0971d0c6",
    category_name: "Motherboard",
  },
  // {
  //   category_id: "462c9208-9d11-42a3-868c-6b85ceebdb01",
  //   category_name: "Graphic Card",
  // },
  {
    category_id: "e2ec7862-300c-4dcc-9fdb-650e5ddd8180",
    category_name: "Cabinet",
  },
  {
    category_id: "a725335d-fef3-489c-ac5d-e3d609e77e29",
    category_name: "CPU Cooler",
  },
  // {
  //   category_id: "18b90cd7-8616-4e0f-b009-cd9ab544b9f5",
  //   category_name: "Cooling Fans",
  // },
  {
    category_id: "ccc2426f-df6b-4e6a-b14a-ce7c29918126",
    category_name: "SMPS",
  },
];

interface Dependencies {
  cpu_motherboard: string;
  motherboard_memory: string;
  // motherboard_cabinet: string;
}

// Actions column background color
const ACTIONS_COLUMN_BG_COLOR = "#e3f2fd";

type SelectedRow = ItemType & {
  tax_rate: number; // equals gst but editable
  discount: number; // numeric field
  discount_type: "%" | "flat";
  taxable_value: number; // selling price - discount
  tax_amount: number; // total tax on item
  item_amount: number; // final price (after discount + tax)
  original_selling_price?: number; // original selling price from database (for validation)
  brand_name?: string;
};

const CreateTemplate = () => {
  document.title = "Create Template";

  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedRow[]>([]);

  const [categories, setCategories] = useState<Category[]>([]);
  const [ITEM_DETAIL_KEYS, setItemKeys] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<{
    template_name?: string;
    description?: string;
    keywords?: string[];
    item_details?: Record<string, any[]>;
  }>({});

  const [dependencies, setDependencies] = useState({
    cpu_motherboard: "",
    motherboard_memory: "",
    // motherboard_cabinet: "",
  });

  const handleBack = useHandleBack("/template");
  const dispatch = useDispatch<any>();
  const { id } = useParams();

  // Fetch All the categories to show rows
  useEffect(() => {
    // After fetching categories
    const fetchCategoriesList = async () => {
      setCategoriesLoading(true);
      try {
        let response = await Axios.get("/api/category/category-dropdown/");
        const updatedCat: Category[] = response.data.data.map((cat: any) => ({
          ...cat,
        }));

        setCategories(updatedCat);

        // Dynamically create ITEM_DETAIL_KEYS
        const KEYS = updatedCat.map(
          (cat) =>
            cat.category_name
              .toLowerCase() // lowercase
              .replace(/\s+/g, "_") // replace spaces with underscores
              .replace(/[^a-z0-9_]/g, "") // remove special characters if needed
        );

        setItemKeys(KEYS);

        console.log("ITEM_DETAIL_KEYS", ITEM_DETAIL_KEYS);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategoriesList();
  }, []);

  // Edit template
  // useEffect(() => {
  //   if (!id) return;
  //   setIsLoading(true);
  //   dispatch(fetchTemplateById(id))
  //     .unwrap()
  //     .then((data: any) => {
  //       const newInitialData = {
  //         template_name: data.template_name,
  //         description: data.description,
  //         keywords: data.keywords,
  //         item_details: data.item_details,
  //       };
  //       setInitialData(newInitialData);

  //       // Pre-populate selected items and update dependencies
  //       const selectedItems: SelectedRow[] = [];
  //       const newDependencies = {
  //         cpu_motherboard: "",
  //         motherboard_memory: "",
  //         motherboard_cabinet: "",
  //       };

  //       Object.entries(data.item_details).forEach(([categoryName, items]) => {
  //         // tell TypeScript that items is an array
  //         const itemArray = items as any[];
  //         itemArray.forEach((item: any) => {
  //           if (item.item_id) {
  //             selectedItems.push({
  //               ...item,
  //               category: item.category,
  //               category_name: item.category_name || categoryName, // optional fallback
  //               quantity: item.quantity || 1,
  //             });

  //             // Update dependencies based on CPU and Motherboard items
  //             if (item.category === "743cffc9-09ae-4509-be91-314361e7bb33") {
  //               // CPU
  //               const socketAttr = item.attributes?.find((a: any) =>
  //                 ["Processor-socket-type", "processor_socket_type"].includes(
  //                   a.attribute_name
  //                 )
  //               );
  //               if (socketAttr) {
  //                 newDependencies.cpu_motherboard =
  //                   socketAttr.manage_value_name;
  //               }
  //             }

  //             if (item.category === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
  //               // Motherboard
  //               const formFactorAttr = item.attributes?.find((a: any) =>
  //                 [
  //                   "Motherboard form factor",
  //                   "motherboard_form_factor",
  //                 ].includes(a.attribute_name)
  //               );
  //               const ramTypeAttr = item.attributes?.find((a: any) =>
  //                 ["Ram Type", "ram_type"].includes(a.attribute_name)
  //               );
  //               if (formFactorAttr) {
  //                 newDependencies.motherboard_cabinet =
  //                   formFactorAttr.manage_value_name;
  //               }
  //               if (ramTypeAttr) {
  //                 newDependencies.motherboard_memory =
  //                   ramTypeAttr.manage_value_name;
  //               }
  //             }
  //           }
  //         });
  //       });
  //       setSelected(selectedItems);
  //       setDependencies(newDependencies);
  //     })
  //     .catch(() => {
  //       toast.error("Failed to fetch Template");
  //     })
  //     .finally(() => setIsLoading(false));
  // }, [id, dispatch]);

  // Edit Template with latest items
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);

    const fetchTemplateAndItems = async () => {
      try {
        const templateData: any = await dispatch(
          fetchTemplateById(id)
        ).unwrap();

        const newInitialData = {
          template_name: templateData.template_name,
          description: templateData.description,
          keywords: templateData.keywords,
          item_details: templateData.item_details,
        };
        setInitialData(newInitialData);

        // Flatten items for easier processing
        const itemsToProcess: any[] = [];
        Object.entries(templateData.item_details).forEach(
          ([categoryName, items]) => {
            const itemArray = items as any[];
            itemArray.forEach((item) => {
              itemsToProcess.push({ ...item, category_name: categoryName });
            });
          }
        );

        // Fetch latest data for each item in parallel
        const updatedItems = await Promise.all(
          itemsToProcess.map(async (item) => {
            try {
              const latestItem: any = await dispatch(
                fetchLatestItemById(item.item_id)
              ).unwrap();
              return { ...item, ...latestItem, quantity: item.quantity }; // merge with latest
            } catch (err) {
              console.warn(`Failed to fetch latest item ${item.item_id}`, err);
              return item; // fallback to existing
            }
          })
        );

        // Rebuild selected items and dependencies
        const selectedItems: SelectedRow[] = [];
        const newDependencies = {
          cpu_motherboard: "",
          motherboard_memory: "",
          // motherboard_cabinet: "",
        };

        updatedItems.forEach((item) => {
          selectedItems.push(
            calculateItemValues({
              ...item,
              quantity: item.quantity || 1,
              tax_rate: Number(item.gst),
              gst: Number(item.gst),
              discount: item.discount ?? 0,
              discount_type: item.discount_type ?? "%",
              original_selling_price: item.original_selling_price ?? item.selling_price,
              brand: item.brand, // Preserve brand ID
              brand_name: item.brand_name, // Preserve brand name
            })
          );

          // Update dependencies based on CPU and Motherboard
          if (item.category === "743cffc9-09ae-4509-be91-314361e7bb33") {
            const socketAttr = item.attributes?.find((a: any) =>
              // ["Processor-socket-type", "processor_socket_type", "Processor Socket Type"].includes(
              ["Processor Socket Type"].includes(
                a.attribute_name
              )
            );
            if (socketAttr)
              newDependencies.cpu_motherboard = socketAttr.manage_value_name;
          }

          if (item.category === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
            // const formFactorAttr = item.attributes?.find((a: any) =>
            //   ["Motherboard form factor", "motherboard_form_factor", "Motherboard Form Factor"].includes(
            //     a.attribute_name
            //   )
            // );
            const ramTypeAttr = item.attributes?.find((a: any) =>
              // ["Ram Type", "ram_type"].includes(a.attribute_name)
              ["RAM Type", "RAM Support"].includes(a.attribute_name)
            );
            // if (formFactorAttr)
            //   newDependencies.motherboard_cabinet =
            //     formFactorAttr.manage_value_name;
            if (ramTypeAttr)
              newDependencies.motherboard_memory =
                ramTypeAttr.manage_value_name;
          }
        });

        setSelected(selectedItems);
        setDependencies(newDependencies);
      } catch (err) {
        console.error("Failed to fetch template or items", err);
        toast.error("Failed to fetch Template");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplateAndItems();
  }, [id, dispatch]);

  const formik = useFormik({
    initialValues: {
      template_name: initialData.template_name || "",
      description: initialData.description || "",
      keywords: initialData.keywords || [],
    },
    enableReinitialize: true,
    // enableReinitialize: true,
    validationSchema: Yup.object({
      template_name: Yup.string()
        .required("Please enter template name")
        .test("template-toast", "Please enter template name", function (value) {
          if (!value || !value.trim()) {
            toast.error("Please enter template name");
            return false;
          }
          return true;
        }),
      description: Yup.string().nullable(),
      // keywords: Yup.array()
      //   .of(Yup.string())
      //   .test(
      //     "keywords-toast",
      //     "Please add at least one keyword",
      //     function (val) {
      //       if (!val || val.length === 0) {
      //         toast.error("Please add at least one keyword");
      //         return false;
      //       }
      //       return true;
      //     }
      //   ),
    }),
    onSubmit: async (values, { resetForm }) => {
      console.log(values);
      try {
        // Check for selected items
        if (selected.length === 0) {
          toast.error("Please add at least one item to the template");
          return;
        }

        // Check for keywords
        if (values.keywords.length === 0) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          toast.error("Please add at least one keyword");
          return;
        }

        const item_details: Record<string, any[]> = {};
        // ITEM_DETAIL_KEYS.forEach((key) => (item_details[key] = []));

        // Map selected UI items to API shape
        selected.forEach((it) => {
          const roundedTotal = getDisplayRoundedTotal(it);
          const payloadRow = {
            item_id: it.item_id,
            category: it.category,
            category_name: it.category_name,
            item_name: it.item_name,
            item_sku: it.item_sku,
            selling_price: Number(it.selling_price ?? 0).toFixed(2),
            hsn_code: null,
            hsn_name: null,
            gst: Number(it.gst ?? 0).toFixed(2),
            tax_rate: it.tax_rate, // add tax_rate
            discount: it.discount, // add discount
            discount_type: it.discount_type, // add discount_type
            taxable_value: roundedTotal, // keep payload total aligned with displayed total
            tax_amount: it.tax_amount, // add tax_amount
            item_amount: roundedTotal, // keep payload total aligned with displayed total
            quantity: it.quantity,
            remarks: "",
            tags: null,
            tag_name: null,
            image: null,
            attributes: it.attributes,
            brand: it.brand, // Preserve brand ID
            brand_name: it.brand_name, // Preserve brand name
          };

          if (!item_details[it.category_name]) {
            item_details[it.category_name] = [];
          }
          item_details[it.category_name].push(payloadRow);
        });

        const payload = {
          ...values,
          template_name: values.template_name,
          description: values.description,
          keywords: values.keywords,
          item_details,
          template_id: id, // Add template_id for update
        };

        try {
          let response;
          console.log(payload);
          if (id) {
            response = await dispatch(updateTemplate(payload)).unwrap();
          } else {
            response = await dispatch(addTemplate(payload)).unwrap();
          }

          console.log("TEMPLATE_SUBMIT", payload);

          toast.success(
            response?.message ||
              (id
                ? "Template updated successfully"
                : "Template added successfully")
          );
          handleBack();
          // reset form fields
          resetForm({
            values: {
              template_name: "",
              description: "",
              keywords: [],
            },
          });
          setSelected([]);
        } catch (error: any) {
          // console.log(error.errors.template_name[0]);
          toast.error(formatLabel(error.errors.template_name[0]));
        }
      } catch (error) {
        toast.error("Error Saving Template:" + error);
      }
    },
  });

  const addItem = (categoryId: string, item: ItemType) => {
    setSelected((prev: SelectedRow[]) => {
      const exists = prev.find(
        (p: SelectedRow) =>
          p.item_id === item.item_id && p.category === categoryId
      );

      if (
        RESTRICTED_SINGLE_ITEM_CATEGORIES.some(
          (c) => c.category_id === categoryId
        )
      ) {
        const alreadyExists = prev.some((p: any) => p.category === categoryId);
        if (alreadyExists) {
          const categoryName = RESTRICTED_SINGLE_ITEM_CATEGORIES.find(
            (c) => c.category_id === categoryId
          )?.category_name;
          toast.error(`Only one ${categoryName} can be added`);
          return prev;
        }
        setOpenCategoryId(null);
      }

      // Auto close accordion for all categories after selection
      setOpenCategoryId(null);

      if (exists) {
        return prev.map((p: SelectedRow) =>
          p.item_id === item.item_id &&
          p.category === categoryId &&
          p.quantity < 10
            ? calculateItemValues({
                ...p,
                quantity: Math.min(10, (p.quantity || 1) + 1),
              })
            : p
        );
      }

      // Pass the new item through calculateItemValues
      const newItem: SelectedRow = calculateItemValues({
        ...item,
        category: categoryId,
        tax_rate: Number(item.gst),
        gst: Number(item.gst),
        quantity: 1,
        discount: 0,
        discount_type: "%",
        brand: item.brand, // Preserve brand ID
        brand_name: item.brand_name, // Preserve brand name
      });

      return [...prev, newItem];
    });

    // Update dependencies after item selection
    const updateDependency = (key: keyof Dependencies, attrNames: string[]) => {
      for (const attrName of attrNames) {
        const attr = item.attributes.find((a) => a.attribute_name === attrName);
        if (attr) {
          setDependencies((prev) => ({
            ...prev,
            [key]: attr.manage_value_name,
          }));
          break; // Stop at first match
        }
      }
    };
    console.log(item);

    if (item.category === "743cffc9-09ae-4509-be91-314361e7bb33") {
      // CPU ID
      // updateDependency("cpu_motherboard", [
      //   "Processor-socket-type",
      //   "processor_socket_type",
      //   "Processor Socket Type",
      // ]);
      updateDependency("cpu_motherboard", ["Processor Socket Type"]);
    }

    // Motherboard category
    if (item.category === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
      // Motherboard ID
      // updateDependency("motherboard_cabinet", [
      //   "Motherboard form factor",
      //   "motherboard_form_factor",
      //   "Motherboard Form Factor",
      // ]);
      // updateDependency("motherboard_memory", ["Ram Type", "ram_type", "RAM Type"]);
      updateDependency("motherboard_memory", ["RAM Type", "RAM Support"]);
    }
  };

  const updateItemField = (
    item_id: string,
    categoryId: string,
    field: string,
    value: any
  ) => {
    setSelected((prev) =>
      prev.map((item) =>
        item.item_id === item_id && item.category === categoryId
          ? calculateItemValues({ ...item, [field]: value })
          : item
      )
    );
  };

  const removeItem = (itemId: string, categoryId: string) => {
    setSelected((prev) => {
      // Special handling for CPU deletion - also remove motherboard, memory and cabinet items
      if (categoryId === "743cffc9-09ae-4509-be91-314361e7bb33") {  
        // CPU category ID - also remove motherboard, memory and cabinet
        const motherboardCategoryId = "14872373-f2f1-45ed-802e-81ef0971d0c6"; // Motherboard category ID
        const memoryCategoryId = "54f1c6b9-481a-4a16-a70e-bfe49341adf8"; // Memory category ID
        // const cabinetCategoryId = "e2ec7862-300c-4dcc-9fdb-650e5ddd8180"; // Cabinet category ID

        const filteredItems = prev.filter(
          (p) =>
            !(p.item_id === itemId && p.category === categoryId) && // Remove the CPU
            p.category !== motherboardCategoryId && // Remove all motherboard items
            p.category !== memoryCategoryId 
            // && // Remove all memory items
            // p.category !== cabinetCategoryId // Remove all cabinet items
        );

        // Show toast notification if motherboard, memory or cabinet items were removed
        const removedMotherboardItems = prev.filter(
          (p) => p.category === motherboardCategoryId
        );
        const removedMemoryItems = prev.filter(
          (p) => p.category === memoryCategoryId
        );
        // const removedCabinetItems = prev.filter(
        //   (p) => p.category === cabinetCategoryId
        // );

        if (
          removedMotherboardItems.length > 0 ||
          removedMemoryItems.length > 0 
          // ||
          // removedCabinetItems.length > 0
        ) {
          // Close all accordions
          setOpenCategoryId(null);
          toast.info(
            "CPU removed - motherboard, memory items have also been cleared"
            // "CPU removed - motherboard, memory, and cabinet items have also been cleared"
          );
        }

        return filteredItems;
      }

      // Special handling for motherboard deletion - also remove memory and cabinet items
      if (categoryId === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
        // Motherboard category ID - also remove memory and cabinet
        const memoryCategoryId = "54f1c6b9-481a-4a16-a70e-bfe49341adf8"; // Memory category ID
        // const cabinetCategoryId = "e2ec7862-300c-4dcc-9fdb-650e5ddd8180"; // Cabinet category ID

        const filteredItems = prev.filter(
          (p) =>
            !(p.item_id === itemId && p.category === categoryId) && // Remove the motherboard
            p.category !== memoryCategoryId 
            // && // Remove all memory items
            // p.category !== cabinetCategoryId // Remove all cabinet items
        );

        // Show toast notification if memory or cabinet items were removed
        const removedMemoryItems = prev.filter(
          (p) => p.category === memoryCategoryId
        );
        // const removedCabinetItems = prev.filter(
          // (p) => p.category === cabinetCategoryId
        // );

        if (removedMemoryItems.length > 0 
          // || removedCabinetItems.length > 0
        ) {
          // Close all accordions
          setOpenCategoryId(null);
          toast.info(
            "Motherboard removed - memory items have also been cleared"
            // "Motherboard removed - memory and cabinet items have also been cleared"
          );
        }

        return filteredItems;
      }

      // Normal removal for other categories
      return prev.filter(
        (p) => !(p.item_id === itemId && p.category === categoryId)
      );
    });
  };

  const setQty = (itemId: string, categoryId: string, qty: number) => {
    setSelected((prev) =>
      prev.map((p) =>
        p.item_id === itemId && p.category === categoryId
          ? calculateItemValues({
              ...p,
              quantity: Math.max(1, Math.min(10000, Math.floor(qty || 1))),
            })
          : p
      )
    );
  };

  const rowsByCategory = useMemo(() => {
    const map: Record<string, SelectedRow[]> = {};
    selected.forEach((r) => {
      map[r.category] = map[r.category] ? [...map[r.category], r] : [r];
    });
    return map;
  }, [selected]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(val);

  const roundUsingFirstDecimal = (value: number) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;

    const absNum = Math.abs(num);
    const normalized = absNum.toFixed(2);
    const [intPart, decimalPart = "00"] = normalized.split(".");

    const integerPart = Number(intPart || "0");
    const firstDecimalDigit = Number(decimalPart[0] || "0");

    let rounded = integerPart;
    if (firstDecimalDigit >= 5) rounded += 1;

    return num < 0 ? -rounded : rounded;
  };

  const getDisplayRoundedTotal = (item: any) => {
    const quantity = Math.max(
      1,
      Math.min(10000, Math.floor(Number(item.quantity) || 1))
    );
    const sellingPrice = Number(item.selling_price || 0);
    const discountValue = Number(item.discount || 0);
    const discountType = String(item.discount_type || "%");

    const lineTotal = sellingPrice * quantity;
    const discountAmount =
      discountType === "%"
        ? (discountValue / 100) * lineTotal
        : discountValue;

    const netAmount = Math.max(0, lineTotal - discountAmount);
    return roundUsingFirstDecimal(netAmount);
  };

  // Derive unlocked categories based on selection
  const unlockedCategories = useMemo(() => {
    if (categories.length === 0) return [];

    const first = categories[0]?.category_id;
    const second = categories[1]?.category_id;

    const selectedFirst = selected.some((s) => s.category === first);
    const selectedSecond = selected.some((s) => s.category === second);

    if (!selectedFirst) {
      // Only first unlocked
      return [first];
    } else if (!selectedSecond) {
      // First selected → unlock first + second
      return [first, second];
    } else {
      // First + second selected → unlock all
      return categories.map((c) => c.category_id);
    }
  }, [categories, selected]);

  //*// Auto open CPU
  // useEffect(() => {
  //   if (categories.length > 0 && !id) {
  //     setOpenCategoryId(categories[0].category_id); // auto-open first row
  //   }
  // }, [categories]);

  useEffect(() => {
    // Only scroll to top if no accordions are open
    if (
      formik.submitCount > 0 &&
      Object.keys(formik.errors).length > 0 &&
      !openCategoryId // assuming openCategoryId is falsy when all are closed
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      // toast.error("Please fix the errors highlighted in the form.");
    }
  }, [formik.errors, formik.submitCount, openCategoryId]);

  return (
    <div className="page-content bg-light min-vh-100">
      <Container fluid className="p-3 p-lg-4 bg-white shadow-sm rounded mx-2">
        {/* Header */}
        <Row className="align-items-center g-2">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
            {/* Left: Back icon and heading */}
            <div className="d-flex align-items-center gap-2">
              <span
                style={{ fontSize: "24px", cursor: "pointer" }}
                onClick={handleBack}
              >
                ←
              </span>
              <h5 className="mb-0 fw-bold">
                {id ? "Edit Template" : "Create Template"}
              </h5>
            </div>

            {/* Right: Breadcrumb */}
            <div className="text-muted">
              <span
                onClick={handleBack}
                style={{ color: "#6c757d", cursor: "pointer" }}
              >
                Template
              </span>{" "}
              &gt;{" "}
              <span style={{ color: "#6c757d", cursor: "default" }}>
                {id ? "Edit Template" : "Create Template"}
              </span>
            </div>
          </div>
        </Row>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            <h5>Template Details</h5>
            <form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* template name+details */}
              <Row className="mb-4 bgback rounded-3 p-2">
                <Col lg={4}>
                  <label className="form-label">
                    Template Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="template_name"
                    className={`form-control ${
                      formik.touched.template_name &&
                      formik.errors.template_name
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder="Enter Template Name"
                    value={formik.values.template_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.template_name &&
                    formik.errors.template_name && (
                      <div className="invalid-feedback">
                        {formik.errors.template_name}
                      </div>
                    )}
                </Col>
                <Col lg={4}>
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    name="description"
                    className="form-control"
                    placeholder="Enter Description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Col>

                <Col lg={4}>
                  <label className="form-label">
                    Keywords <span className="text-danger">*</span>
                  </label>
                  <Autocomplete
                    multiple
                    freeSolo
                    value={[]} // keep input field empty
                    onChange={(_, newValue) => {
                      const current = formik.values.keywords || [];
                      const updated = [
                        ...current,
                        ...newValue.filter((v: any) => !current.includes(v)),
                      ];
                      formik.setFieldValue("keywords", updated);
                    }}
                    options={[]}
                    renderInput={(params: any) => {
                      // function to reuse in both Enter & Button
                      const handleAdd = () => {
                        const value = params.inputProps?.value?.trim();
                        if (value && !formik.values.keywords.includes(value)) {
                          formik.setFieldValue("keywords", [
                            ...formik.values.keywords,
                            value,
                          ]);
                        }
                        // clear input field
                        if (params.inputProps?.onChange) {
                          const nativeInput = params.inputProps as any;
                          nativeInput.onChange({ target: { value: "" } });
                        }
                      };

                      return (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <TextField
                            {...params}
                            placeholder="Add Keywords"
                            size="small"
                            variant="outlined"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAdd();
                              }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              style: {
                                height: "38px",
                                padding: "0 0.5rem",
                                backgroundColor: "#ffffffff",
                                borderRadius: "6px",
                                border: "1px solid #fffefeff",
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": { borderColor: "#ffffffff" },
                                "&:hover fieldset": {
                                  borderColor: "#f9f7f7ff",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#1976d2",
                                  borderWidth: "0.5px",
                                },
                                backgroundColor: "#fffefeff",
                              },
                              "& .MuiInputBase-input::placeholder": {
                                fontSize: "0.8rem",
                                opacity: 0.6,
                                fontFamily: "Poppins, sans-serif",
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "0.8rem",
                                fontFamily: "Poppins, sans-serif",
                              },
                            }}
                          />
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAdd}
                            style={{ height: "38px" }}
                          >
                            Add
                          </Button>
                        </div>
                      );
                    }}
                  />

                  {/* Display selected keywords */}
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {formik.values.keywords.map((kw, idx) => (
                      <Chip
                        key={idx}
                        label={kw}
                        size="small"
                        variant="outlined"
                        onDelete={() =>
                          formik.setFieldValue(
                            "keywords",
                            formik.values.keywords.filter((k) => k !== kw)
                          )
                        }
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          borderColor: "#0073BB",
                          color: "#0073BB",
                          "& .MuiChip-deleteIcon": {
                            color: "#0073BB",
                          },
                        }}
                      />
                    ))}
                  </div>
                </Col>
              </Row>

              <hr />

              {/* Item Details */}

              <h5 className="mx-2 my-4">Items</h5>

              <LoaderErrorWrapper loading={categoriesLoading}>
                <div className="table-responsive table-card rounded-4 mx-0 m-lg-1 my-1 border">
                  <Table className="table table-bordered table-centered align-middle table-custom-effect table-nowrap mb-0">
                    <thead
                      className="table-abgb"
                      style={{ backgroundColor: "#0070b6ff", color: "#fff" }}
                    >
                      <tr>
                        <th style={{ color: "#fff" }} className="px-3">
                          Category
                        </th>
                        {selected.length > 0 ? (
                          <>
                            <th style={{ color: "#fff" }} className="px-3">
                              Item Name
                            </th>
                            <th style={{ color: "#fff" }} className="px-3">
                              Brand
                            </th>
                            <th style={{ color: "#fff" }} className="px-3">
                              Item SKU
                            </th>
                            <th style={{ color: "#fff" }} className="px-3">
                              Selling Price
                            </th>
                            <th style={{ color: "#fff" }} className="px-3">
                              Quantity
                            </th>
                            <th style={{ color: "#fff" }} className="px-3">
                              Discount
                            </th>
                            {/* <th style={{ color: "#fff" }} className="px-3">
                              GST %
                            </th> */}
                            <th style={{ color: "#fff" }} className="px-3">
                              Total
                            </th>
                          </>
                        ) : (
                          <th
                            style={{ color: "#fff" }}
                            className="px-3"
                            colSpan={7}
                          >
                            Selection
                          </th>
                        )}
                        <th
                          style={{
                            color: "#fff",
                            width: 120,
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#0070b6ff",
                            zIndex: 10,
                            borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                          }}
                          className="px-3"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Mandatory categories first */}
                      {categories
                        .filter((cat) => cat.is_mandatory)
                        .map((cat) => {
                          const selectedRows =
                            rowsByCategory[cat.category_id] || [];
                          const isOpen = openCategoryId === cat.category_id;

                          return (
                            <React.Fragment key={cat.category_id}>
                              {selectedRows.length === 0 ? (
                                // When nothing is selected in this category
                                <tr>
                                  {
                                    <td className="px-3">
                                      {formatLabel(cat.category_name)}
                                    </td>
                                  }
                                  <td colSpan={7} className="text-muted px-3">
                                    Choose{" "}
                                    {cat.category_name.includes("(")
                                      ? cat.category_name.split("(")[0].trim()
                                      : cat.category_name}
                                  </td>
                                  <td
                                    className="px-3"
                                    style={{
                                      position: "sticky",
                                      right: 0,
                                      backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                                      zIndex: 9,
                                      borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                                    }}
                                  >
                                    <Button
                                      variant="light"
                                      onClick={() => {
                                        if (
                                          unlockedCategories.includes(
                                            cat.category_id
                                          )
                                        ) {
                                          setOpenCategoryId(
                                            isOpen ? null : cat.category_id
                                          );
                                        }
                                      }}
                                      disabled={
                                        !unlockedCategories.includes(
                                          cat.category_id
                                        )
                                      }
                                      style={{
                                        backgroundColor:
                                          unlockedCategories.includes(
                                            cat.category_id
                                          )
                                            ? "#0070b6ff"
                                            : "#ff0015ff",
                                        color: "white", // to make the icon visible
                                        border: "none",
                                        padding: "0px 8px",
                                      }}
                                    >
                                      <i
                                        className={
                                          openCategoryId === cat.category_id
                                            ? "bi bi-dash-lg"
                                            : "bi bi-plus"
                                        }
                                        style={{ fontSize: "1.5rem" }}
                                      />
                                    </Button>
                                  </td>
                                </tr>
                              ) : (
                                // Render one row per selected item
                                selectedRows.map((item, idx) => {
                                  // const total =
                                  //   r.quantity * r.price * (1 + r.tax / 100);

                                  const brandName = item.brand_name ||
                                    item.attributes?.find(
                                      (attr: any) =>
                                        attr.attribute_name === "Brand" ||
                                        attr.attribute_name === "brand"
                                    )?.manage_value_name || "-";

                                  return (
                                    <tr
                                      key={`${cat.category_id}-${item.item_id}`}
                                    >
                                      <td className="px-3">
                                        {idx === 0 && cat.category_name}
                                      </td>
                                      <td
                                        className="text-truncate"
                                        style={{ maxWidth: 300 }}
                                        title={item.item_name}
                                      >
                                        {item.item_name}
                                      </td>
                                      <td
                                        className="text-truncate"
                                        style={{ maxWidth: 150 }}
                                      >
                                        {brandName}
                                      </td>
                                      <td
                                        className="text-truncate"
                                        style={{ maxWidth: 250 }}
                                      >
                                        {item.item_sku}
                                      </td>
                                      <td style={{ maxWidth: 100 }}>
                                        {formatCurrency(item.selling_price)}
                                      </td>

                                      <td style={{ maxWidth: 120 }}>
                                        <QuantityInput
                                          value={item.quantity}
                                          maxQty={10}
                                          disabled={RESTRICTED_SINGLE_ITEM_CATEGORIES.some(
                                            (restricted) =>
                                              restricted.category_id ===
                                              cat.category_id
                                          )}
                                          onChange={(val) =>
                                            setQty(
                                              item.item_id,
                                              item.category,
                                              val
                                            )
                                          }
                                        />
                                      </td>

                                      {/* Discount column */}
                                      <td className="d-flex gap-2 align-items-center">
                                        <Form.Control
                                          type="number"
                                          value={item.discount}
                                          size="sm"
                                          className="form-control form-control-sm text-center"
                                          style={{
                                            width: "60px",
                                            height: "34px",
                                            padding: "0.25rem",
                                          }}
                                          onChange={(e) => {
                                            let val = Number(e.target.value);

                                            if (item.discount_type === "%") {
                                              // Clamp percentage discount between 0 and 100
                                              val = Math.max(
                                                0,
                                                Math.min(val, 100)
                                              );
                                            } else {
                                              // Flat discount cannot exceed total line price
                                              const maxFlatDiscount =
                                                item.selling_price *
                                                item.quantity;
                                              val = Math.max(
                                                0,
                                                Math.min(val, maxFlatDiscount)
                                              );
                                            }

                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "discount",
                                              val
                                            );
                                          }}
                                        />
                                        <Form.Select
                                          value={item.discount_type}
                                          size="sm"
                                          onChange={(e) =>
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "discount_type",
                                              e.target.value
                                            )
                                          }
                                          className="text-center"
                                          style={{
                                            width: "45px",
                                            height: "34px",
                                            padding: 0, // remove internal padding completely
                                            lineHeight: "1", // align text vertically
                                            border: "1px solid #ced4da", // optional, keep consistent with row
                                            borderRadius: "0", // remove rounded corners if you want flush
                                            appearance: "none",
                                            WebkitAppearance: "none",
                                            MozAppearance: "none",
                                            // background: "transparent", // optional, avoid extra background
                                            background: `url("data:image/svg+xml;utf8,<svg fill='black' height='10' viewBox='0 0 24 24' width='10' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>") no-repeat right 4px center`,
                                            backgroundColor: "white",
                                            backgroundSize: "12px",
                                          }}
                                        >
                                          <option value="%">%</option>
                                          <option value="flat">₹</option>
                                        </Form.Select>
                                      </td>

                                      {/* Tax rate (GST) editable */}
                                      {/* <td style={{ maxWidth: 60 }}>
                                        <input
                                          type="number"
                                          value={item.gst}
                                          readOnly
                                          // onChange={(e) => {
                                          //   const val = Math.max(
                                          //     0,
                                          //     Math.min(
                                          //       Number(e.target.value),
                                          //       100
                                          //     )
                                          //   );
                                          //   updateItemField(
                                          //     item.item_id,
                                          //     item.category,
                                          //     "tax_rate",
                                          //     val
                                          //   );
                                          // }}
                                          className="form-control form-control-sm text-center bg-light"
                                        />
                                      </td> */}
                                      <td>
                                        {formatCurrency(
                                          getDisplayRoundedTotal(item)
                                        )}
                                      </td>

                                      <td
                                        style={{
                                          position: "sticky",
                                          right: 0,
                                          backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                                          zIndex: 9,
                                          borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                          }}
                                        >
                                          {/* Remove Button */}
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              padding: 0,
                                            }}
                                            onClick={() =>
                                              removeItem(
                                                item.item_id,
                                                item.category
                                              )
                                            }
                                          >
                                            <i className="bi bi-trash" />
                                          </Button>

                                          {/* Accordion Button - only for first row of category */}
                                          {idx === 0 && (
                                            <Button
                                              size="sm"
                                              onClick={() => {
                                                if (
                                                  unlockedCategories.includes(
                                                    cat.category_id
                                                  )
                                                ) {
                                                  setOpenCategoryId(
                                                    isOpen
                                                      ? null
                                                      : cat.category_id
                                                  );
                                                }
                                              }}
                                              disabled={
                                                !unlockedCategories.includes(
                                                  cat.category_id
                                                )
                                              }
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                                padding: 0,
                                                backgroundColor:
                                                  unlockedCategories.includes(
                                                    cat.category_id
                                                  )
                                                    ? "#0070b6ff"
                                                    : "#ff0015ff",
                                                color: "white",
                                                border: "none",
                                              }}
                                            >
                                              <i
                                                className={
                                                  openCategoryId ===
                                                  cat.category_id
                                                    ? "bi bi-dash-lg"
                                                    : "bi bi-plus"
                                                }
                                                style={{ fontSize: "1rem" }}
                                              />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}

                              {/* Accordion row for adding more items */}
                              <tr>
                                <td
                                  colSpan={10}
                                  style={{ border: 0, paddingTop: 0 }}
                                >
                                  <Accordion
                                    activeKey={isOpen ? "0" : undefined}
                                  >
                                    <Accordion.Item eventKey="0">
                                      <Accordion.Body style={{ padding: 0 }}>
                                        <Suspense fallback={<LoadingCard />}>
                                          {isOpen && (
                                            <DynamicItems
                                              addItem={addItem}
                                              categoryId={cat.category_id}
                                              categoryName={cat.category_name}
                                              dependencies={dependencies}
                                              setDependencies={setDependencies}
                                            />
                                          )}
                                        </Suspense>
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  </Accordion>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}

                      {/* Separator row */}
                      <tr>
                        <td
                          colSpan={10}
                          className="fw-bold text-center"
                          style={{
                            border: "none", // remove table borders
                            height: "100%", // allow full height
                            padding: "1rem 0", // give vertical space
                          }}
                        >
                          <div
                            className="bgback"
                            style={{
                              width: "100%",
                              padding: "0.75rem",
                              borderRadius: "10px",
                              alignContent: "left",
                            }}
                          >
                            Additional Items
                          </div>
                        </td>
                      </tr>

                      {/* Optional categories */}
                      {categories
                        .filter((cat) => !cat.is_mandatory)
                        .map((cat) => {
                          const selectedRows =
                            rowsByCategory[cat.category_id] || [];
                          const isOpen = openCategoryId === cat.category_id;

                          return (
                            <React.Fragment key={cat.category_id}>
                              {selectedRows.length === 0 ? (
                                // When nothing is selected in this category
                                <tr>
                                  {
                                    <td className="px-3">
                                      {formatLabel(cat.category_name)}
                                    </td>
                                  }
                                  <td colSpan={7} className="text-muted px-3">
                                    Choose{" "}
                                    {cat.category_name.includes("(")
                                      ? cat.category_name.split("(")[0].trim()
                                      : cat.category_name}
                                  </td>
                                  <td
                                    className="px-3"
                                    style={{
                                      position: "sticky",
                                      right: 0,
                                      backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                                      zIndex: 9,
                                      borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                                    }}
                                  >
                                    <Button
                                      variant="light"
                                      onClick={() => {
                                        if (
                                          unlockedCategories.includes(
                                            cat.category_id
                                          )
                                        ) {
                                          setOpenCategoryId(
                                            isOpen ? null : cat.category_id
                                          );
                                        }
                                      }}
                                      disabled={
                                        !unlockedCategories.includes(
                                          cat.category_id
                                        )
                                      }
                                      style={{
                                        backgroundColor:
                                          unlockedCategories.includes(
                                            cat.category_id
                                          )
                                            ? "#0070b6ff"
                                            : "#ff0015ff",
                                        color: "white", // to make the icon visible
                                        border: "none",
                                        padding: "0px 8px",
                                      }}
                                    >
                                      <i
                                        className={
                                          openCategoryId === cat.category_id
                                            ? "bi bi-dash-lg"
                                            : "bi bi-plus"
                                        }
                                        style={{ fontSize: "1.5rem" }}
                                      />
                                    </Button>
                                  </td>
                                </tr>
                              ) : (
                                // Render one row per selected item
                                selectedRows.map((item, idx) => {
                                  // const total =
                                  //   r.quantity * r.price * (1 + r.tax / 100);

                                  const brandName = item.brand_name ||
                                    item.attributes?.find(
                                      (attr: any) =>
                                        attr.attribute_name === "Brand" ||
                                        attr.attribute_name === "brand"
                                    )?.manage_value_name || "-";

                                  return (
                                    <tr
                                      key={`${cat.category_id}-${item.item_id}`}
                                    >
                                      <td className="px-3">
                                        {idx === 0 && cat.category_name}
                                      </td>
                                      <td
                                        className="text-truncate"
                                        style={{ maxWidth: 300 }}
                                        title={item.item_name}
                                      >
                                        {item.item_name}
                                      </td>
                                      <td
                                        className="text-truncate"
                                        style={{ maxWidth: 150 }}
                                      >
                                        {brandName}
                                      </td>
                                      <td
                                        className="text-truncate"
                                        style={{ maxWidth: 250 }}
                                      >
                                        {item.item_sku}
                                      </td>
                                      <td style={{ maxWidth: 100 }}>
                                        {formatCurrency(item.selling_price)}
                                      </td>

                                      <td style={{ maxWidth: 120 }}>
                                        <QuantityInput
                                          value={item.quantity}
                                          maxQty={10}
                                          disabled={RESTRICTED_SINGLE_ITEM_CATEGORIES.some(
                                            (restricted) =>
                                              restricted.category_id ===
                                              cat.category_id
                                          )}
                                          onChange={(val) =>
                                            setQty(
                                              item.item_id,
                                              item.category,
                                              val
                                            )
                                          }
                                        />
                                      </td>

                                      {/* Discount column */}
                                      <td className="d-flex gap-2 align-items-center">
                                        <Form.Control
                                          type="number"
                                          value={item.discount}
                                          size="sm"
                                          className="form-control form-control-sm text-center"
                                          style={{
                                            width: "60px",
                                            height: "34px",
                                            padding: "0.25rem",
                                          }}
                                          onChange={(e) => {
                                            let val = Number(e.target.value);

                                            if (item.discount_type === "%") {
                                              // Clamp percentage discount between 0 and 100
                                              val = Math.max(
                                                0,
                                                Math.min(val, 100)
                                              );
                                            } else {
                                              // Flat discount cannot exceed total line price
                                              const maxFlatDiscount =
                                                item.selling_price *
                                                item.quantity;
                                              val = Math.max(
                                                0,
                                                Math.min(val, maxFlatDiscount)
                                              );
                                            }

                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "discount",
                                              val
                                            );
                                          }}
                                        />
                                        <Form.Select
                                          value={item.discount_type}
                                          size="sm"
                                          onChange={(e) =>
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "discount_type",
                                              e.target.value
                                            )
                                          }
                                          className="text-center"
                                          style={{
                                            width: "45px",
                                            height: "34px",
                                            padding: 0, // remove internal padding completely
                                            lineHeight: "1", // align text vertically
                                            border: "1px solid #ced4da", // optional, keep consistent with row
                                            borderRadius: "0", // remove rounded corners if you want flush
                                            appearance: "none",
                                            WebkitAppearance: "none",
                                            MozAppearance: "none",
                                            // background: "transparent", // optional, avoid extra background
                                            background: `url("data:image/svg+xml;utf8,<svg fill='black' height='10' viewBox='0 0 24 24' width='10' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>") no-repeat right 4px center`,
                                            backgroundColor: "white",
                                            backgroundSize: "12px",
                                          }}
                                        >
                                          <option value="%">%</option>
                                          <option value="flat">₹</option>
                                        </Form.Select>
                                      </td>

                                      {/* Tax rate (GST) editable */}
                                      {/* <td style={{ maxWidth: 60 }}>
                                        <input
                                          type="number"
                                          value={item.gst}
                                          readOnly
                                          // onChange={(e) => {
                                          //   const val = Math.max(
                                          //     0,
                                          //     Math.min(
                                          //       Number(e.target.value),
                                          //       100
                                          //     )
                                          //   );
                                          //   updateItemField(
                                          //     item.item_id,
                                          //     item.category,
                                          //     "tax_rate",
                                          //     val
                                          //   );
                                          // }}
                                          className="form-control form-control-sm text-center bg-light"
                                        />
                                      </td> */}
                                      <td>
                                        {formatCurrency(
                                          getDisplayRoundedTotal(item)
                                        )}
                                      </td>

                                      <td
                                        style={{
                                          position: "sticky",
                                          right: 0,
                                          backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                                          zIndex: 9,
                                          borderLeft: "2px solid rgba(0, 0, 0, 0.1)"
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                          }}
                                        >
                                          {/* Remove Button */}
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              padding: 0,
                                            }}
                                            onClick={() =>
                                              removeItem(
                                                item.item_id,
                                                item.category
                                              )
                                            }
                                          >
                                            <i className="bi bi-trash" />
                                          </Button>

                                          {/* Accordion Button - only for first row of category */}
                                          {idx === 0 && (
                                            <Button
                                              size="sm"
                                              onClick={() => {
                                                if (
                                                  unlockedCategories.includes(
                                                    cat.category_id
                                                  )
                                                ) {
                                                  setOpenCategoryId(
                                                    isOpen
                                                      ? null
                                                      : cat.category_id
                                                  );
                                                }
                                              }}
                                              disabled={
                                                !unlockedCategories.includes(
                                                  cat.category_id
                                                )
                                              }
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                                padding: 0,
                                                backgroundColor:
                                                  unlockedCategories.includes(
                                                    cat.category_id
                                                  )
                                                    ? "#0070b6ff"
                                                    : "#ff0015ff",
                                                color: "white",
                                                border: "none",
                                              }}
                                            >
                                              <i
                                                className={
                                                  openCategoryId ===
                                                  cat.category_id
                                                    ? "bi bi-dash-lg"
                                                    : "bi bi-plus"
                                                }
                                                style={{ fontSize: "1rem" }}
                                              />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}

                              {/* Accordion row for adding more items */}
                              <tr>
                                <td
                                  colSpan={10}
                                  style={{ border: 0, paddingTop: 0 }}
                                >
                                  <Accordion
                                    activeKey={isOpen ? "0" : undefined}
                                  >
                                    <Accordion.Item eventKey="0">
                                      <Accordion.Body style={{ padding: 0 }}>
                                        <Suspense fallback={<LoadingCard />}>
                                          {isOpen && (
                                            <DynamicItems
                                              addItem={addItem}
                                              categoryId={cat.category_id}
                                              categoryName={cat.category_name}
                                              dependencies={dependencies}
                                              setDependencies={setDependencies}
                                            />
                                          )}
                                        </Suspense>
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  </Accordion>
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-center gap-2 mt-4">
                  <Button className="btn btn-secondary" onClick={handleBack}>
                    Cancel
                  </Button>
                  {/* <Button type="submit" variant="primary">
                    Save
                  </Button> */}

                  <SubmitButton
                    isSubmitting={formik.isSubmitting}
                    isEdit={!!id}
                    labels={{
                      create: "Save",
                      update: "Update",
                      submitting: "Saving...",
                      updating: "Updating...",
                    }}
                  />
                </div>
              </LoaderErrorWrapper>
            </form>
          </>
        )}
      </Container>
    </div>
  );
};

export default CreateTemplate;
